import type { IUpdateArticleStockCmd } from "@/data/commands/catalog/UpdateArticleStockCmd";
import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IStock, IStockTableViewArticle } from "@/data/model/Article";

import { logError } from "@/shared/logging/errors";

import { Popup } from "@/components/dialog/Popup";

import { NumberInput } from "@/operator/elements/NumberInput";
import { trans } from "@/operator/localization/Trans";

export class StockTableEditor {
    public $element: JQuery;
    private $editor: JQuery;
    private $saveBtn?: JQuery;
    private $disableWarning?: JQuery;
    private initial?: IStock;
    private remainingInput?: NumberInput;
    private articleId: string;
    private isAvailable: boolean;
    private isDisabled: boolean;

    public onSaved?: { (m: IStockTableViewArticle) };

    constructor(articleId: string, isAvailable: boolean, stock: IStock | undefined, isDisabled: boolean) {
        this.articleId = articleId;
        this.isAvailable = isAvailable;
        this.isDisabled = isDisabled;

        this.$element = $('<div class="stockeditor" >');
        this.$editor = $('<div class="editor" />').appendTo(this.$element);

        const remainingInput = $('<input type="text" class="form-control" name="remaining" />').appendTo(this.$editor);
        this.remainingInput = new NumberInput(remainingInput, {
            min: 0,
            step: 1,
        });
        this.remainingInput.setDisabled(isDisabled);

        this.setByModel(stock);

        this.$saveBtn = $(`<button type="button" class="btn btn-success">${trans.general.save}</button>`).appendTo(
            this.$element
        );

        if (!isDisabled) {
            this.remainingInput.onChange = () => {
                this.checkSaveBtn();
                this.checkArticleDisableWarning();
            };

            this.$saveBtn.click(() => {
                this.save();
            });
        }

        this.checkSaveBtn();
    }

    private setByModel(st?: IStock) {
        this.initial = st;

        if (st === undefined) this.setUntracked();
        else {
            this.setTracked();
        }
    }

    private setUntracked() {
        this.remainingInput!.setDisabled(true).setValue("");
        if (this.$disableWarning !== undefined) {
            this.$disableWarning.remove();
            this.$disableWarning = undefined;
        }
    }

    private setTracked() {
        this.remainingInput!.setDisabled(this.isDisabled).setValue(
            this.initial === undefined ? 1 : this.initial.remaining
        );
    }

    private checkSaveBtn() {
        const newValue: IStock | undefined = {
            remaining: this.remainingInput!.getValue()!,
        };

        const hasChanges =
            (this.initial === undefined && newValue !== undefined) ||
            (this.initial !== undefined && newValue === undefined) ||
            this.initial?.remaining !== newValue?.remaining;

        this.$saveBtn?.prop("disabled", this.isDisabled || !hasChanges);
    }

    private checkArticleDisableWarning() {
        let shouldShow = false;
        if (this.$saveBtn !== undefined && this.isAvailable) {
            const checkSafety =
                this.initial === undefined || this.initial.safetyStock === undefined ? 0 : this.initial.safetyStock;
            if (this.remainingInput!.getValue()! <= checkSafety) {
                shouldShow = true;
            }
        }

        if (shouldShow && this.$disableWarning === undefined) {
            this.$disableWarning = $(
                `<div class="disablewarning" title="${trans.items.disableWarning}"><i class="flaticon-danger"></i></div>`
            ).insertBefore(this.$editor);
        } else if (!shouldShow && this.$disableWarning !== undefined) {
            this.$disableWarning.remove();
            this.$disableWarning = undefined;
        }
    }

    private save() {
        this.$saveBtn!.prop("disabled", true);
        const isActive = true;

        const data: IUpdateArticleStockCmd = {
            isActive: isActive,
            safetyStock: !isActive ? undefined : this.initial === undefined ? undefined : this.initial.safetyStock,
            notifyThreshold: !isActive
                ? undefined
                : this.initial === undefined
                  ? undefined
                  : this.initial.notifyThreshold,
            remaining: !isActive ? undefined : this.remainingInput!.getValue(),
        };

        Factory.getInstance<CatalogDao>(CatalogDao)
            .setArticleStock(this.articleId, data)
            .then((b) => {
                this.isAvailable = b.isAvailable;
                this.setByModel(b.stock);
                this.$saveBtn!.prop("disabled", false);
                this.checkSaveBtn();
                this.checkArticleDisableWarning();

                if (this.onSaved !== undefined) this.onSaved(b);
            })
            .catch((ex) => {
                logError(ex);
                this.$saveBtn!.prop("disabled", false);
                Popup.show({
                    message: trans.items.stockSaveFailed,
                });
            });
    }

    public destroy() {
        this.remainingInput!.destroy();
        this.remainingInput = undefined;
        this.$element.remove();
    }

    public appendTo($element: JQuery): StockTableEditor {
        this.$element.appendTo($element);

        return this;
    }
}
